import React, { useEffect } from 'react';

import { CommandGroup } from 'components/commandPalette/base/CommandGroup';
import Icon from 'components/lib/ui/Icon';

import useEventCallback from 'common/lib/hooks/useEventCallback';
import typewriter from 'lib/analytics/typewriter';
import { useCommandRegistry, useGetCommandsByGroup } from 'lib/commands/context';
import type { MonarchCommandGroup } from 'lib/commands/types';
import { useDispatch } from 'lib/hooks';
import useTheme from 'lib/hooks/useTheme';
import { setUiThemePreference, toggleSidebarExpanded } from 'state/persistentUi/actions';

import type { UiTheme } from 'common/types';

const GROUP: MonarchCommandGroup = 'settings';

/**
 * A component that renders all settings-related commands in the command palette.
 */
export const SettingsCommands: React.FC = () => {
  const commands = useGetCommandsByGroup(GROUP);

  return (
    <CommandGroup.Root group={GROUP} heading="Settings">
      {commands.map((command) => (
        <CommandGroup.Item key={command.id} {...command} />
      ))}
    </CommandGroup.Root>
  );
};

/**
 * A hook that registers all settings-related commands.
 * Separate from the UI rendering component.
 */
export const useRegisterSettingsCommands = (): void => {
  const { registerCommand } = useCommandRegistry();
  const dispatch = useDispatch();
  const { uiTheme } = useTheme();
  const isDark = uiTheme === 'dark';

  const toggleTheme = useEventCallback(() => {
    const preference: UiTheme = isDark ? 'light' : 'dark';
    dispatch(setUiThemePreference(preference));
    typewriter.visualAppearancePreferenceUpdated({ preference });
  });

  const toggleLeftNavigation = useEventCallback(() => {
    dispatch(toggleSidebarExpanded());
  });

  // Register all settings commands
  useEffect(() => {
    registerCommand({
      group: GROUP,
      id: 'toggle-left-navigation',
      label: 'Toggle left navigation',
      action: toggleLeftNavigation,
      keys: ['['],
      icon: <Icon name="sidebar" />,
      keywords: ['sidebar', 'expand', 'navigation', 'collapse'],
      analyticsMetadata: {
        analyticsId: 'toggle-left-navigation',
      },
    });

    // It's important to unbind the toggle theme command when the component unmounts
    // because the icon changes when the theme changes. Without unbinding the icon,
    // the icon will be stale. We might want to revisit this to allow commands to hold
    // references to the icon somehow.
    return registerCommand({
      group: GROUP,
      id: 'toggle-theme',
      label: 'Toggle theme',
      action: toggleTheme,
      keys: ['alt+shift+t'],
      keywords: ['light', 'dark'],
      icon: <Icon name={uiTheme === 'dark' ? 'sun' : 'moon'} />,
      analyticsMetadata: {
        analyticsId: 'toggle-theme',
      },
    });
  }, [toggleTheme, registerCommand, toggleLeftNavigation, uiTheme]);
};
